import { useCallback, useEffect, useRef, useState } from 'react';
import { useClickOutside } from '../../hooks/useClickOutside';
import { ChevronDownIcon } from '../../icons/ChevronDownIcon';
import {
  Chevron,
  Option,
  OptionsList,
  Placeholder,
  Trigger,
  TriggerText,
  Wrapper,
} from './Dropdown.styles';
import type { DropdownProps } from './Dropdown.types';

export function Dropdown({
  options,
  value,
  placeholder = 'Select',
  onChange,
  disabled,
  'aria-label': ariaLabel,
}: DropdownProps) {
  const [isOpen, setIsOpen] = useState(false);
  const [activeIndex, setActiveIndex] = useState(-1);
  const wrapperRef = useRef<HTMLDivElement>(null);
  const listRef = useRef<HTMLUListElement>(null);
  const triggerRef = useRef<HTMLButtonElement>(null);

  const handleClose = useCallback(() => setIsOpen(false), []);
  useClickOutside(wrapperRef, handleClose, isOpen);

  useEffect(() => {
    if (!isOpen) return;
    const handler = (e: KeyboardEvent) => {
      if (e.key === 'Escape') {
        setIsOpen(false);
        triggerRef.current?.focus();
      }
    };
    document.addEventListener('keydown', handler);
    return () => document.removeEventListener('keydown', handler);
  }, [isOpen]);

  useEffect(() => {
    if (isOpen) {
      const selectedIdx = options.findIndex((o) => o.value === value);
      setActiveIndex(selectedIdx >= 0 ? selectedIdx : 0);
    }
  }, [isOpen, options, value]);

  useEffect(() => {
    if (!isOpen || activeIndex < 0) return;
    const items = listRef.current?.querySelectorAll('[role="option"]');
    if (items && items[activeIndex]) {
      (items[activeIndex] as HTMLElement).scrollIntoView?.({ block: 'nearest' });
    }
  }, [isOpen, activeIndex]);

  const selectedOption = options.find((o) => o.value === value);

  const handleSelect = (optionValue: string) => {
    onChange(optionValue);
    setIsOpen(false);
    triggerRef.current?.focus();
  };

  const handleKeyDown = (e: React.KeyboardEvent) => {
    if (!isOpen) {
      if (e.key === 'ArrowDown' || e.key === 'ArrowUp' || e.key === 'Enter' || e.key === ' ') {
        e.preventDefault();
        setIsOpen(true);
      }
      return;
    }

    switch (e.key) {
      case 'ArrowDown': {
        e.preventDefault();
        setActiveIndex((prev) => {
          let next = prev + 1;
          while (next < options.length && options[next].disabled) next++;
          return next < options.length ? next : prev;
        });
        break;
      }
      case 'ArrowUp': {
        e.preventDefault();
        setActiveIndex((prev) => {
          let next = prev - 1;
          while (next >= 0 && options[next].disabled) next--;
          return next >= 0 ? next : prev;
        });
        break;
      }
      case 'Home': {
        e.preventDefault();
        const first = options.findIndex((o) => !o.disabled);
        if (first >= 0) setActiveIndex(first);
        break;
      }
      case 'End': {
        e.preventDefault();
        for (let i = options.length - 1; i >= 0; i--) {
          if (!options[i].disabled) {
            setActiveIndex(i);
            break;
          }
        }
        break;
      }
      case 'Enter':
      case ' ': {
        e.preventDefault();
        if (activeIndex >= 0 && !options[activeIndex].disabled) {
          handleSelect(options[activeIndex].value);
        }
        break;
      }
    }
  };

  const activeOptionId =
    isOpen && activeIndex >= 0 ? `dropdown-option-${options[activeIndex]?.value}` : undefined;

  return (
    <Wrapper ref={wrapperRef}>
      <Trigger
        ref={triggerRef}
        type="button"
        onClick={() => !disabled && setIsOpen(!isOpen)}
        onKeyDown={handleKeyDown}
        data-open={isOpen}
        disabled={disabled}
        aria-label={ariaLabel}
        aria-expanded={isOpen}
        aria-haspopup="listbox"
        aria-activedescendant={activeOptionId}
      >
        <TriggerText>
          {selectedOption ? selectedOption.label : <Placeholder>{placeholder}</Placeholder>}
        </TriggerText>
        <Chevron $open={isOpen}>
          <ChevronDownIcon size={14} />
        </Chevron>
      </Trigger>
      {isOpen && (
        <OptionsList ref={listRef} role="listbox" aria-label={ariaLabel}>
          {options.map((option, index) => (
            <Option
              key={option.value}
              id={`dropdown-option-${option.value}`}
              role="option"
              aria-selected={option.value === value}
              aria-disabled={option.disabled}
              data-selected={option.value === value}
              data-disabled={option.disabled}
              data-active={index === activeIndex}
              onClick={() => !option.disabled && handleSelect(option.value)}
            >
              {option.label}
            </Option>
          ))}
        </OptionsList>
      )}
    </Wrapper>
  );
}
